import { Component, inject, input, output, model, signal, OnInit, OnDestroy, effect, computed, untracked } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subscription, firstValueFrom, startWith } from 'rxjs';
import * as QRCode from 'qrcode';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule, FormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { InventoryService } from '@servicios/inventory/inventory.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { SelectModule } from 'primeng/select';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { ChipsModule } from 'primeng/chips';
import { ConfigService } from '@servicios/config.service';
import { DatePickerModule } from 'primeng/datepicker';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { MeterGroupModule } from 'primeng/metergroup';
import { PopoverModule } from 'primeng/popover';
import { PipeService } from '@servicios/pipe.service';
import { FileAttachmentsComponent } from '@sharedComponents/file-attachments/file-attachments.component';
import { UserService } from '@servicios/user.service';
import { AuthenticationService } from '@servicios/authentication.service';
import { ItemLabelComponent } from '../../item-label/item-label.component';
import { TestItemBplDataComponent } from '../test-item-bpl-data/test-item-bpl-data.component';
import { ConfiguracionesTecnicasService } from '@servicios/configuracionesTecnicas.service';
import { Roles, TechConfigModels } from '@interfaces/operation-result-types';

import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ProgressBarModule } from 'primeng/progressbar';

@Component({
  selector: 'app-item-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    FileAttachmentsComponent,
    ItemLabelComponent,
    ButtonModule,
    TabsModule,
    SelectModule,
    AutoCompleteModule,
    ChipsModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    DividerModule,
    InputTextModule,
    TextareaModule,
    MultiSelectModule,
    DatePickerModule,
    TableModule,
    TooltipModule,
    MeterGroupModule,
    PopoverModule,
    DialogModule,
    TagModule,
    ToggleSwitchModule,
    ProgressBarModule,
    TestItemBplDataComponent
  ],
  templateUrl: './item-form.component.html',
  styleUrl: './item-form.component.scss'
})
export class ItemFormComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private subs = new Subscription();
  private stockValuesSignal = signal({ stock: 0, min_stock: 0, alert_stock: 0, max_stock: 100 });
  form: FormGroup = this.fb.group({
    id: [''],
    v_: [0],
    name: ['', [Validators.required, Validators.minLength(3)]],
    commercial_name: [''],
    internal_code: ['', [Validators.required]],
    barcode: [''],
    description: [''],
    type: ['PRODUCT', [Validators.required]],
    brandId: [null, [Validators.required]],
    supplierId: [null],
    formId: [null, [Validators.required]],
    locationId: [null],
    matrixId: [null],
    wasteTypeId: [null],
    wasteClassSolid: [''],
    wasteClassLiquid: [''],
    stock: [0, [Validators.required, Validators.min(0)]],
    min_stock: [0, [Validators.required, Validators.min(0)]],
    max_stock: [0, [Validators.required, Validators.min(0)]],
    price: [0, [Validators.min(0)]],
    currency: ['USD'],
    tags: [[]],
    regulationIds: [[]],
    alert_stock: [0, [Validators.required, Validators.min(0)]],
    // SERVICE specific fields
    service_periodicity: [null],
    service_next_date: [null],
    service_contract_start: [null],
    service_contract_end: [null],
    service_scope: [''],
    service_responsible: [''],
    service_unit: [''],
    // Technical & Compliance fields
    cas_number: [''],
    lot_number: [''],
    expiration_date: [null],
    storage_conditions: [''],
    hazard_class: [''],
    sds_url: [''],
    std_certificate_number: [''],
    std_valid_until: [null],
    std_traceability_to: [''],
    std_uncertainty_value: [''],
    // TEST ITEM (Agrochemicals) specific fields
    formulationTypeId: [null],
    studyId: [null],
    toxicology: [null],
    physical_state: [null],
    request_date: [null],
    reception_date: [null],
    presentation: [''],
    sponsor: [''],
    composition: this.fb.array([]),
    fractions: this.fb.array([]),
    received_quantity: [null],
    received_unit_id: [null],
    toxicology_reference: [''],
    experimental_remaining: [null],
    observations: [''],
    // Dual Inventory & Fractional Consumption
    consumption_mode: ['DISCRETE'],
    nominal_content: [null],
    content_unit: [''],
    in_use_stability_days: [null],
    container_prefix: [''],
    container_numbering_mode: ['LOT_PREFIXED'],
    reorder_content_threshold_percent: [20, [Validators.min(1), Validators.max(100)]],
    reorder_stability_percent: [80, [Validators.min(1), Validators.max(100)]],
    requires_analysis: [false]
  });

  consumptionModes = [
    { label: 'Por Envase Completo (Discreto)', value: 'DISCRETE' },
    { label: 'Por Contenido / Alícuotas (Fraccional)', value: 'FRACTIONAL' }
  ];

  containerNumberingModes = [
    { label: 'Prefijo de Lote (ej: [LOTE]-E01)', value: 'LOT_PREFIXED' },
    { label: 'Correlativo Global (ej: PAT-0001)', value: 'GLOBAL_SEQUENTIAL' },
    { label: 'Manual / Por Escaneo de Código', value: 'MANUAL' }
  ];


  private inventoryService = inject(InventoryService);
  private messageService = inject(MessageService);
  private pipeService = inject(PipeService);
  private userService = inject(UserService);
  private techConfigService = inject(ConfiguracionesTecnicasService);
  private configService = inject(ConfigService);
  private confirmationService = inject(ConfirmationService);
  private authService = inject(AuthenticationService);

  canManageInventory = computed(() => {
    const roles = this.authService.CURRENT_USER_ROLES || [];
    return roles.includes(Roles.ADMIN) || roles.includes(Roles.INVENTORY_MANAGER);
  });

  canViewPrices = computed(() => {
    const roles = this.authService.CURRENT_USER_ROLES || [];
    return roles.includes(Roles.ADMIN) || roles.includes(Roles.INVENTORY_MANAGER);
  });

  get massBalanceActive(): boolean {
    return !!this.configService.appSettings?.frontend?.modules?.laboratory?.samples?.massBalance?.active;
  }

  // Derivations
  get translatedType() {
    const type = this.item()?.id ? this.item()?.type : this.form.get('type')?.value;
    return this.pipeService.transform(type?.toUpperCase(), 'item_type');
  }

  typeSignal = signal<string>('PRODUCT');
  isService = computed(() => this.typeSignal() === 'SERVICE');
  isReactive = computed(() => this.typeSignal() === 'REACTIVE');
  isStandard = computed(() => this.typeSignal() === 'STANDARD');
  isTestItem = computed(() => this.typeSignal() === 'TEST_ITEM');
  isConsumable = computed(() => this.typeSignal() === 'CONSUMABLE' || this.typeSignal() === 'REACTIVE' || this.typeSignal() === 'PRODUCT' || this.typeSignal() === 'TEST_ITEM');

  placeholders = computed(() => {
    const service = this.isService();
    const testItem = this.isTestItem();
    return {
      name: service ? 'Ej: Calibración Anual de Balanza' : 'Ej: Acetona 99% Pureza',
      code: testItem ? '(Se generará al guardar)' : (service ? 'SERV-001' : 'ART-001'),
      description: service ? 'Detalle del alcance del servicio, entregables y condiciones...' : 'Uso detallado, precauciones o especificaciones técnicas...'
    };
  });

  // Inputs & Outputs
  item = input<any>(null); // Initial data
  mode = input<'view' | 'edit' | 'create'>('edit');
  activeTab = model<number>(0);

  // State for Movements & Equipments
  itemMovements = signal<any[]>([]);
  loadingMovements = signal(false);
  itemConsumptions = signal<any[]>([]);
  loadingConsumptions = signal(false);
  linkedEquipments = signal<any[]>([]);
  allEquipments = signal<any[]>([]);
  selectedEquipmentToLink = signal<string | null>(null);
  itemForecast = signal<any>(null);
  qrCodeImage = signal<string | null>(null);
  locations = signal<any[]>([]);
  commercialNamesFiltered = signal<string[]>([]);
  printItemData = signal<any | null>(null);
  printPartitionData = signal<any | null>(null);

  // Lots & FEFO/FIFO State
  itemLots = signal<any[]>([]);
  loadingLots = signal<boolean>(false);
  lotDialogVisible = signal<boolean>(false);
  isSavingLot = signal<boolean>(false);
  editingLotId = signal<string | null>(null);

  consumptionStrategy = computed(() => {
    const lots = this.itemLots();
    const hasExpiringLots = lots.some(l => l.expiration_date && (l.status === 'ACTIVE' || l.current_quantity > 0));
    return hasExpiringLots ? 'FEFO' : 'FIFO';
  });

  consumptionModeSignal = signal<string>('DISCRETE');

  isFractional = computed(() => {
    return this.consumptionModeSignal() === 'FRACTIONAL' || this.item()?.consumption_mode === 'FRACTIONAL';
  });

  // Fractional Containers State
  containers = signal<any[]>([]);
  loadingContainers = signal<boolean>(false);
  showConsumeDialog = signal<boolean>(false);
  selectedContainer = signal<any>(null);
  showContainerConsumptionsDialog = signal<boolean>(false);
  selectedContainerForConsumptions = signal<any>(null);
  isConsuming = signal<boolean>(false);

  consumeForm: FormGroup = this.fb.group({
    quantity: [null, [Validators.required, Validators.min(0.0001)]],
    reason: [''],
    sample_code: [null]
  });

  lotForm: FormGroup = this.fb.group({
    lot_number: ['', [Validators.required, Validators.minLength(1)]],
    internal_lot_code: [''],
    expiration_date: [null],
    reception_date: [new Date(), Validators.required],
    initial_quantity: [1, [Validators.required, Validators.min(0)]],
    current_quantity: [1, [Validators.required, Validators.min(0)]],
    locationId: [null],
    unit_price: [null],
    currency: ['ARS'],
    status: ['ACTIVE', Validators.required],
    notes: [''],
    container_codes_text: [''],
  });

  async searchCommercialName(event: any) {
    const query = event.query;
    try {
      const results = await this.inventoryService.getCommercialNames(query);
      this.commercialNamesFiltered.set(results);
    } catch (e) {
      this.commercialNamesFiltered.set([]);
    }
  }

  // Meter Group Logic (Visual Stock representation)
  private router = inject(Router);

  viewMassBalance() {
    console.log('viewMassBalance triggered in ItemForm', this.item());
    if (this.item()?.id) {
      this.router.navigateByUrl(`/laboratory/mass-balance/${this.item().id}`).then(success => {
        if (!success) console.error('Navigation failed to /laboratory/mass-balance');
      }).catch(err => {
        console.error('Navigation error:', err);
      });
    }
  }

  private updateStockValues() {
    const raw = this.form.getRawValue();
    const stock = Number(raw.stock) || 0;
    const min_stock = Number(raw.min_stock) || 0;
    const alert_stock = Number(raw.alert_stock) || 0;
    const max_stock = Number(raw.max_stock) || stock || 100;
    this.stockValuesSignal.set({ stock, min_stock, alert_stock, max_stock });
  }

  stockMeterItems = computed(() => {
    const { stock, min_stock, alert_stock } = this.stockValuesSignal();

    const items = [
      { label: 'Crítico', value: Math.min(stock, min_stock), color: '#ef4444', icon: 'pi pi-exclamation-triangle' },
      { label: 'Alerta', value: stock > min_stock ? Math.min(stock - min_stock, alert_stock - min_stock) : 0, color: '#f59e0b', icon: 'pi pi-exclamation-circle' },
      { label: 'Óptimo', value: stock > alert_stock ? stock - alert_stock : 0, color: '#22c55e', icon: 'pi pi-check-circle' }
    ];

    return items.filter(i => i.value > 0);
  });

  onSave = output<any>();
  onCancel = output<void>();
  onBack = output<void>();
  onModeChange = output<'view' | 'edit'>();

  // State
  isSaving = signal(false);

  // Options
  brands = signal<any[]>([]);
  suppliers = signal<any[]>([]);
  forms = signal<any[]>([]);
  regulations = signal<any[]>([]);
  wasteTypes = signal<any[]>([]);
  wasteClassOptions = signal<any[]>([]);
  appUsers = signal<any[]>([]);
  units = signal<any[]>([]);
  matrixOptions = signal<any[]>([]);

  contentUnitOptions = computed(() => {
    const base = this.units().map((u: any) => ({
      label: u.label,
      value: u.label
    }));
    const current = this.form.get('content_unit')?.value;
    if (current && !base.some(b => b.value === current)) {
      base.push({ label: current, value: current });
    }
    return base.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  });

  itemTypes = [
    { label: 'Producto',   value: 'PRODUCT',    icon: 'pi pi-box',        description: 'Artículos físicos con control de stock.' },
    { label: 'Consumible', value: 'CONSUMABLE',  icon: 'pi pi-wave-pulse', description: 'Insumos que se agotan con el uso diario.' },
    { label: 'Reactivo',   value: 'REACTIVE',   icon: 'pi pi-filter',     description: 'Sustancias químicas para análisis.' },
    { label: 'Estándar',   value: 'STANDARD',   icon: 'pi pi-shield',     description: 'Patrones de referencia o calibración.' },
    { label: 'Repuesto',   value: 'SPARE_PART', icon: 'pi pi-wrench',     description: 'Componentes para mantenimiento.' },
    { label: 'Servicio',   value: 'SERVICE',    icon: 'pi pi-briefcase',  description: 'Items no físicos o intangibles.' },
    { label: 'Ítem de Ensayo', value: 'TEST_ITEM', icon: 'pi pi-filter',   description: 'Muestras de ensayo con composición y balance de masa (BPL).' }
  ];

  formulationTypes = signal<any[]>([]);

  toxicologyClasses = [
    { label: 'Clase I', value: 'I' },
    { label: 'Clase II', value: 'II' },
    { label: 'Clase III', value: 'III' }
  ];

  physicalStates = [
    { label: 'Sólido', value: 'SOLID' },
    { label: 'Líquido', value: 'LIQUID' }
  ];

  currencies = [
    { label: 'USD - Dólar Estadounidense', value: 'USD' },
    { label: 'ARS - Peso Argentino', value: 'ARS' }
  ];

  servicePeriodicities = [
    { label: 'Mensual', value: 'Mensual' },
    { label: 'Trimestral', value: 'Trimestral' },
    { label: 'Semestral', value: 'Semestral' },
    { label: 'Anual', value: 'Anual' },
    { label: 'Bianual', value: 'Bianual' },
    { label: 'Única vez', value: 'Unica' }
  ];

  constructor() {
    // Subscribe to type changes to update signals
    const typeSub = this.form.get('type')?.valueChanges.pipe(startWith(this.form.get('type')?.value)).subscribe(val => {
        if (val) this.typeSignal.set(val.toUpperCase());
    });
    if (typeSub) this.subs.add(typeSub);

    const consumptionModeSub = this.form.get('consumption_mode')?.valueChanges.pipe(startWith(this.form.get('consumption_mode')?.value)).subscribe(val => {
      if (val) this.consumptionModeSignal.set(val);
    });
    if (consumptionModeSub) this.subs.add(consumptionModeSub);

    const formChangesSub = this.form.valueChanges.subscribe(() => {
      this.updateStockValues();
    });
    this.subs.add(formChangesSub);

    // Effect to update form when item input changes
    effect(() => {
      const currentItem = this.item();

      untracked(() => {
        // Reset dependent signals
        this.itemMovements.set([]);
        this.itemConsumptions.set([]);
        this.itemForecast.set(null);
        this.linkedEquipments.set([]);
        this.containers.set([]);

        // ALWAYS reset the form to a base state before applying new values
        // this ensures that fields not present in the new 'item' are cleared
        this.form.reset({
          type: 'PRODUCT',
          v_: null,
          currency: 'USD',
          stock: 0,
          min_stock: 0,
          max_stock: 0,
          alert_stock: 0,
          tags: [],
          regulationIds: [],
          locationId: null,
          matrixId: null,
          wasteTypeId: null,
          wasteClassSolid: '',
          wasteClassLiquid: '',
          service_periodicity: null,
          service_next_date: null,
          service_contract_start: null,
          service_contract_end: null,
          service_scope: '',
          service_responsible: '',
          service_unit: '',
          cas_number: '',
          lot_number: '',
          expiration_date: null,
          storage_conditions: '',
          hazard_class: '',
          sds_url: '',
          std_certificate_number: '',
          std_valid_until: null,
          std_traceability_to: '',
          std_uncertainty_value: '',
          formulation_type: null,
          formulationTypeId: null,
          studyId: null,
          toxicology: null,
          physical_state: null,
          request_date: null,
          reception_date: null,
          presentation: '',
          sponsor: '',
          composition: [],
          fractions: [],
          requires_analysis: false,
          consumption_mode: 'DISCRETE',
          container_numbering_mode: 'LOT_PREFIXED',
          reorder_content_threshold_percent: 20,
          reorder_stability_percent: 80
        }, { emitEvent: false });

        if (currentItem && (currentItem.id || currentItem.name)) {
          this.patchForm(currentItem);
          this.generateQRCode(currentItem);
          if (currentItem.id) {
            this.loadItemLots(currentItem.id);
            this.loadItemMovements(currentItem.id);
            this.loadItemConsumptions(currentItem.id);
            this.loadLinkedEquipments(currentItem.id);
          }
          if (currentItem.type) {
            this.typeSignal.set(currentItem.type.toUpperCase());
          }
        } else if (currentItem && currentItem.type) {
          // For new items with just a type
          this.form.patchValue({ type: currentItem.type }, { emitEvent: false });
          this.qrCodeImage.set(null);
          this.typeSignal.set(currentItem.type.toUpperCase());
          this.applyFormMode(this.mode());
        } else {
          this.typeSignal.set('PRODUCT');
          this.applyFormMode(this.mode());
        }
      });
    });

    // Effect to handle mode changes
    effect(() => {
      const m = this.mode();
      untracked(() => {
        this.applyFormMode(m);
      });
    });

    // Effect to adjust validators based on type
    effect(() => {
      const service = this.isService();
      const testItem = this.isTestItem();
      untracked(() => {
        const brandCtrl = this.form.get('brandId');
        const formIdCtrl = this.form.get('formId');
        const stockCtrl = this.form.get('stock');
        const minStockCtrl = this.form.get('min_stock');
        const alertStockCtrl = this.form.get('alert_stock');
        const maxStockCtrl = this.form.get('max_stock');
        const priceCtrl = this.form.get('price');
        const currencyCtrl = this.form.get('currency');

        // Note: price and currency are now optional globally as per user request
        priceCtrl?.clearValidators();
        currencyCtrl?.clearValidators();

        if (service || testItem) {
          stockCtrl?.clearValidators();
          minStockCtrl?.clearValidators();
          alertStockCtrl?.clearValidators();
          maxStockCtrl?.clearValidators();
          brandCtrl?.clearValidators();
          formIdCtrl?.clearValidators();
        } else {
          brandCtrl?.setValidators([Validators.required]);
          formIdCtrl?.setValidators([Validators.required]);
          stockCtrl?.setValidators([Validators.required, Validators.min(0)]);
          minStockCtrl?.setValidators([Validators.required, Validators.min(0)]);
          alertStockCtrl?.setValidators([Validators.required, Validators.min(0)]);
          maxStockCtrl?.setValidators([Validators.required, Validators.min(0)]);
        }
        const internalCodeCtrl = this.form.get('internal_code');
        if (testItem) {
          // For test items, the internal code is optional on the frontend because it's autogenerated on backend save!
          internalCodeCtrl?.clearValidators();
        } else {
          internalCodeCtrl?.setValidators([Validators.required]);
        }
        internalCodeCtrl?.updateValueAndValidity({ emitEvent: false });

        brandCtrl?.updateValueAndValidity({ emitEvent: false });
        formIdCtrl?.updateValueAndValidity({ emitEvent: false });
        stockCtrl?.updateValueAndValidity({ emitEvent: false });
        minStockCtrl?.updateValueAndValidity({ emitEvent: false });
        alertStockCtrl?.updateValueAndValidity({ emitEvent: false });
        maxStockCtrl?.updateValueAndValidity({ emitEvent: false });
      });
    });
  }

  ngOnInit() {
    this.loadOptions();

    // Auto-generate Test Item name if configured
    if (this.configService.appSettings?.frontend?.general?.testItemNameAutoGenerate) {
      this.form.get('composition')?.valueChanges.subscribe(() => this.autoCalculateTestItemName());
      this.form.get('formulationTypeId')?.valueChanges.subscribe(() => this.autoCalculateTestItemName());
    }
  }

  private autoCalculateTestItemName() {
    if (!this.isTestItem()) return;

    const composition = this.form.get('composition')?.value;
    const formulationTypeId = this.form.get('formulationTypeId')?.value;
    
    if (!composition || composition.length === 0) return;

    let actives: string[] = [];
    let concentrations: string[] = [];

    composition.forEach((comp: any) => {
      if (comp.analite && comp.analite.name) {
        actives.push(comp.analite.name);
      }
      if (comp.concentration !== undefined && comp.concentration !== null) {
        concentrations.push(comp.concentration.toString());
      }
    });

    let formulationCode = '';
    if (formulationTypeId) {
      const groups = this.formulationTypes();
      for (const group of groups) {
        const found = group.items?.find((i: any) => i.value === formulationTypeId);
        if (found) {
          formulationCode = found.original?.code || '';
          break;
        }
      }
    }

    if (actives.length > 0) {
      const activeStr = actives.join(' + ');
      const concStr = concentrations.join(' + ');
      const fullName = `${activeStr} ${concStr} ${formulationCode}`.trim();
      
      this.form.get('name')?.patchValue(fullName, { emitEvent: false });
    }
  }

  analiteValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;
    if (typeof value !== 'object' || !value.id) {
      return { invalidAnalite: true };
    }
    return null;
  };

  private patchForm(item: any) {
    const patchData = {
      ...item,
      name: item.name,
      commercial_name: item.commercial_name || '',
      internal_code: item.internal_code,
      brandId: item.brand?.id || item.brandId || null,
      formId: item.form?.id || item.formId || null,
      stock: item.stock ?? 0,
      min_stock: item.min_stock ?? 0,
      alert_stock: item.alert_stock ?? 0,
      max_stock: item.max_stock ?? 0,
      locationId: item.location?.id || item.locationId || null,
      matrixId: item.matrixId || null,
      wasteTypeId: item.waste_type?.id || item.wasteTypeId || null,
      wasteClassSolid: item.wasteClassSolid || '',
      wasteClassLiquid: item.wasteClassLiquid || '',
      supplierId: (item.suppliers && item.suppliers.length > 0) ? item.suppliers[0].id : (item.supplierId || null),
      regulationIds: item.controlledBy ? item.controlledBy.map((r: any) => r.id) : (item.regulationIds || []),
      formulationTypeId: item.formulation_type?.id || item.formulationTypeId || null,
      // Ensure dates are actual Date objects for the datepicker
      service_next_date: item.service_next_date ? new Date(item.service_next_date) : null,
      service_contract_start: item.service_contract_start ? new Date(item.service_contract_start) : null,
      service_contract_end: item.service_contract_end ? new Date(item.service_contract_end) : null,
      expiration_date: item.expiration_date ? new Date(item.expiration_date) : null,
      std_valid_until: item.std_valid_until ? new Date(item.std_valid_until) : null,
      request_date: item.request_date ? new Date(item.request_date) : null,
      reception_date: item.reception_date ? new Date(item.reception_date) : null,
      experimental_remaining: item.experimental_remaining !== undefined ? item.experimental_remaining : null,
      observations: item.observations || '',
      requires_analysis: item.requires_analysis ?? false,
      consumption_mode: item.consumption_mode || 'DISCRETE',
      nominal_content: item.nominal_content ?? null,
      content_unit: item.content_unit || '',
      in_use_stability_days: item.in_use_stability_days ?? null,
      container_prefix: item.container_prefix || '',
      container_numbering_mode: item.container_numbering_mode || 'LOT_PREFIXED',
      reorder_content_threshold_percent: item.reorder_content_threshold_percent ?? 20,
      reorder_stability_percent: item.reorder_stability_percent ?? 80
    };

    // Safely clear and repopulate FormArrays before patching the rest
    const compositionArray = this.form.get('composition') as FormArray;
    compositionArray.clear();
    if (item.composition && Array.isArray(item.composition)) {
      item.composition.forEach((comp: any) => {
        let analiteVal = comp.analite || null;
        if (analiteVal && typeof analiteVal === 'object') {
          const code = analiteVal.code || analiteVal.abbreviation || analiteVal.analite_code || '';
          analiteVal = {
            ...analiteVal,
            code,
            analite_code: code,
            label: analiteVal.label || `${code ? '[' + code + '] ' : ''}${analiteVal.name || ''}`.trim()
          };
        } else if (typeof analiteVal === 'string' && analiteVal.trim() !== '') {
          analiteVal = {
            id: null,
            name: analiteVal,
            label: `[No vinculado] ${analiteVal}`
          };
        }

        compositionArray.push(this.fb.group({
          analite: [analiteVal, [Validators.required, this.analiteValidator]],
          concentration: [comp.concentration || 0, [Validators.required, Validators.min(0)]],
          unit_id: [comp.unit_id || null, Validators.required],
          order: [comp.order || compositionArray.length + 1]
        }));
      });
    }

    const fractionsArray = this.form.get('fractions') as FormArray;
    fractionsArray.clear();
    if (item.fractions && Array.isArray(item.fractions)) {
      item.fractions.forEach((frac: any) => {
        fractionsArray.push(this.fb.group({
          code: [{ value: frac.code || '', disabled: true }, Validators.required],
          name: [frac.name || frac.destination || '', Validators.required],
          destination: [frac.destination || frac.name || ''],
          tare: [frac.tare !== undefined ? frac.tare : null, [Validators.required, Validators.min(0)]],
          initial_mass: [frac.initial_mass !== undefined ? frac.initial_mass : null, [Validators.required, Validators.min(0)]],
          consumptions: [frac.consumptions || []],
          theoretical_rem: [frac.theoretical_rem !== undefined ? frac.theoretical_rem : null],
          experimental_rem: [frac.experimental_rem !== undefined ? frac.experimental_rem : null],
          difference: [frac.difference !== undefined ? frac.difference : null]
        }));
      });
    }

    this.form.patchValue(patchData, { emitEvent: false });
    this.consumptionModeSignal.set(item.consumption_mode || 'DISCRETE');
    this.updateStockValues();
    this.applyFormMode(untracked(() => this.mode()));
  }

  private applyFormMode(m: 'view' | 'edit' | 'create') {
    if (!this.canManageInventory() || m === 'view') {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.form.enable({ emitEvent: false });

    // Stock on existing items is calculated from lots/movements, not edited by hand
    if (m === 'edit' || this.item()?.id) {
      this.form.get('stock')?.disable({ emitEvent: false });
    }

    // Keep specifically disabled fields disabled
    const fractionsArray = this.form.get('fractions') as FormArray;
    if (fractionsArray) {
      fractionsArray.controls.forEach(ctrl => {
        ctrl.get('code')?.disable({ emitEvent: false });
      });
    }
    if (this.item()?.mass_balance_status === 'CLOSED') {
      this.form.get('received_quantity')?.disable({ emitEvent: false });
      this.form.get('experimental_remaining')?.disable({ emitEvent: false });
      this.form.get('observations')?.disable({ emitEvent: false });
      this.form.get('received_unit_id')?.disable({ emitEvent: false });
      if (fractionsArray) {
        fractionsArray.controls.forEach(ctrl => {
          ctrl.get('tare')?.disable({ emitEvent: false });
          ctrl.get('initial_mass')?.disable({ emitEvent: false });
          ctrl.get('name')?.disable({ emitEvent: false });
        });
      }
    }
  }

  async loadOptions() {
    try {
      const [brandsRes, suppliersRes, formsRes, regulationsRes, wasteTypesRes, locationsRes, equipments, users, formulationTypesRes] = await Promise.all([
        this.inventoryService.getAll('brand'),
        this.inventoryService.getAll('supplier'),
        this.inventoryService.getAll('form'),
        this.inventoryService.getAll('regulations'),
        this.inventoryService.getAll('waste-type'),
        this.inventoryService.getAll('location'),
        this.inventoryService.getAll('equipments', { limit: 1000 }),
        firstValueFrom(this.userService.getUsersList()),
        this.inventoryService.getAll('formulation-types', { limit: 1000 })
      ]);

      this.brands.set(brandsRes.data.map((b: any) => ({ label: b.name, value: b.id })));
      this.suppliers.set(suppliersRes.data.map((s: any) => ({ label: s.name, value: s.id })));
      this.forms.set(formsRes.data.map((f: any) => ({ label: f.name, value: f.id })));
      this.regulations.set(regulationsRes.data.map((r: any) => ({ label: r.name, value: r.id })));
      this.wasteTypes.set(wasteTypesRes.data.map((w: any) => ({ label: w.name, value: w.id })));
      this.locations.set(locationsRes.data || []);
      // Group formulation types by classification
      const grouped = formulationTypesRes.data.reduce((acc: any, ft: any) => {
        const cls = ft.classification || 'Formulados';
        if (!acc[cls]) {
          acc[cls] = { label: cls, icon: cls.includes('Técnico') ? 'pi pi-filter' : 'pi pi-box', items: [] };
        }
        acc[cls].items.push({ label: `${ft.code} - ${ft.name}`, value: ft.id, original: ft });
        return acc;
      }, {});
      this.formulationTypes.set(Object.values(grouped));

      const wasteSub = this.techConfigService._getAll(TechConfigModels.wasteClass).subscribe({
        next: (res) => {
          this.wasteClassOptions.set(res.data.techConfigs.map((c: any) => ({ label: c.value, value: c.value })));
        },
        error: (err) => console.error('Error fetching wasteClass options', err)
      });
      this.subs.add(wasteSub);

      const matrixSub = this.techConfigService._getAll(TechConfigModels.matrix).subscribe({
        next: (res) => {
          const list = res?.data?.techConfigs || [];
          this.matrixOptions.set(list.map((m: any) => ({
            label: `${m.code ? m.code + ' - ' : ''}${m.value || m.name}`,
            value: m.id
          })));
        },
        error: (err) => console.error('Error fetching matrix options', err)
      });
      this.subs.add(matrixSub);

      const usersList = (users as any)?.data?.usersList || (users as any)?.data?.users || [];
      this.appUsers.set(usersList.map((u: any) => ({
        label: `${u.name} ${u.lastName}`,
        value: `${u.name} ${u.lastName}` // Storing the name as requested by the current schema/flow
      })));

      const equipmentsData = equipments?.data || (Array.isArray(equipments) ? equipments : []);
      this.allEquipments.set(equipmentsData.map((e: any) => ({
        label: `${e.brand?.name || ''} ${e.model || ''} - ${e.serial_number || ''}`,
        value: e.id
      })));

      // Load specific item data if editing
      if (this.item()?.id) {
        this.loadItemMovements(this.item().id);
        this.loadLinkedEquipments(this.item().id);
        this.loadItemForecast(this.item().id);
        this.loadItemConsumptions(this.item().id);
      }
      this.loadUnits();
    } catch (error) {
      console.error("Error loading form options", error);
    }
  }

  async loadUnits() {
    try {
      const unitsRes = await firstValueFrom(this.techConfigService._getAll(TechConfigModels.units)) as any;
      if (unitsRes?.data?.techConfigs) {
        this.units.set(unitsRes.data.techConfigs.map((u: any) => ({
          label: u.value || u.name,
          value: u.id
        })));
      }
    } catch (e) {
      console.error('Error loading units', e);
    }
  }

  async loadItemMovements(id: string) {
    try {
      this.loadingMovements.set(true);
      const res = await this.inventoryService.getStockMovementsByItem(id);
      this.itemMovements.set(res.data || []);
    } catch (e) {
      console.error("Error loading movements", e);
    } finally {
      this.loadingMovements.set(false);
    }
  }

  async loadItemConsumptions(id: string) {
    try {
      this.loadingConsumptions.set(true);
      const res = await this.inventoryService.getItemConsumptions(id);
      this.itemConsumptions.set(res || []);
    } catch (e) {
      console.error("Error loading consumptions", e);
    } finally {
      this.loadingConsumptions.set(false);
    }
  }

  async loadItemForecast(id: string) {
    try {
      const res = await this.inventoryService.getItemForecast(id);
      this.itemForecast.set(res);
    } catch (e) {
      console.error("Error loading forecast", e);
    }
  }

  async loadLinkedEquipments(id: string) {
    try {
      const itemWithRelations = await this.inventoryService.getOne('items', id);
      this.linkedEquipments.set(itemWithRelations.equipments || []);
    } catch (e) {
      console.error("Error loading linked equipments", e);
    }
  }

  async linkEquipment() {
    const eqId = this.selectedEquipmentToLink();
    const itemId = this.item()?.id;
    if (!eqId || !itemId) return;

    try {
      await this.inventoryService.linkItemToEquipment(itemId, eqId);
      this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Equipo vinculado' });
      this.selectedEquipmentToLink.set(null);
      this.loadLinkedEquipments(itemId);
    } catch (e) {
      console.error(e);
    }
  }

  async unlinkEquipment(eqId: string) {
    const itemId = this.item()?.id;
    if (!itemId) return;

    try {
      await this.inventoryService.unlinkItemFromEquipment(itemId, eqId);
      this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Vínculo eliminado' });
      this.loadLinkedEquipments(itemId);
    } catch (e) {
      console.error(e);
    }
  }

  getMovementLabel(type: string): string {
    switch (type) {
      case 'IN': return 'Entrada';
      case 'OUT': return 'Salida';
      case 'ADJUSTMENT': return 'Ajuste';
      default: return type;
    }
  }

  // ── Lot & FEFO/FIFO Management Methods ────────────────────────────────────

  async loadItemLots(id?: string) {
    const itemId = id || this.item()?.id;
    if (!itemId) return;
    try {
      this.loadingLots.set(true);
      const lots = await this.inventoryService.getItemLots(itemId);
      this.itemLots.set(lots || []);
      if (this.isFractional()) {
        await this.loadAllContainers(itemId, lots || []);
      }
    } catch (e) {
      console.error('Error loading item lots', e);
    } finally {
      this.loadingLots.set(false);
    }
  }

  goToStockMovementIn() {
    const itemId = this.item()?.id;
    if (!itemId) return;
    this.router.navigate(['/inventory/movements/new'], {
      queryParams: { itemId, type: 'IN' }
    });
  }

  openNewLotDialog() {
    this.editingLotId.set(null);
    this.lotForm.reset({
      lot_number: '',
      internal_lot_code: '',
      expiration_date: null,
      reception_date: new Date(),
      initial_quantity: 1,
      current_quantity: 1,
      locationId: this.form.get('locationId')?.value || null,
      unit_price: this.form.get('price')?.value || null,
      currency: this.form.get('currency')?.value || 'ARS',
      status: 'ACTIVE',
      notes: '',
      container_codes_text: '',
    });
    this.lotDialogVisible.set(true);
  }

  openEditLotDialog(lot: any) {
    this.editingLotId.set(lot.id);
    this.lotForm.patchValue({
      lot_number: lot.lot_number,
      internal_lot_code: lot.internal_lot_code || '',
      expiration_date: lot.expiration_date ? new Date(lot.expiration_date) : null,
      reception_date: lot.reception_date ? new Date(lot.reception_date) : new Date(),
      initial_quantity: lot.initial_quantity,
      current_quantity: lot.current_quantity,
      locationId: lot.locationId || null,
      unit_price: lot.unit_price ? Number(lot.unit_price) : null,
      currency: lot.currency || 'ARS',
      status: lot.status || 'ACTIVE',
      notes: lot.notes || '',
      container_codes_text: '',
    });
    this.lotDialogVisible.set(true);
  }

  async saveLot() {
    if (this.lotForm.invalid) {
      this.lotForm.markAllAsTouched();
      return;
    }

    const itemId = this.item()?.id;
    if (!itemId) return;

    this.isSavingLot.set(true);
    try {
      const formVal = this.lotForm.value;
      const containerCodes = formVal.container_codes_text
        ? formVal.container_codes_text.split(/[\n,]+/).map((s: string) => s.trim()).filter((s: string) => s.length > 0)
        : undefined;

      const payload = {
        ...formVal,
        container_codes: containerCodes,
        internal_lot_code: formVal.internal_lot_code?.trim() || null,
        expiration_date: formVal.expiration_date ? new Date(formVal.expiration_date).toISOString() : null,
        reception_date: formVal.reception_date ? new Date(formVal.reception_date).toISOString() : new Date().toISOString(),
      };

      const lotId = this.editingLotId();
      if (lotId) {
        await this.inventoryService.updateItemLot(itemId, lotId, payload);
        this.messageService.add({ severity: 'success', summary: 'Lote Actualizado', detail: 'El lote se actualizó correctamente' });
      } else {
        await this.inventoryService.createItemLot(itemId, payload);
        this.messageService.add({ severity: 'success', summary: 'Lote Creado', detail: 'El nuevo lote se dio de alta y se actualizó el stock' });
      }

      this.lotDialogVisible.set(false);
      await this.loadItemLots(itemId);
      const updatedItem = await this.inventoryService.getOne('items', itemId);
      if (updatedItem) {
        this.form.patchValue({
          stock: updatedItem.stock,
          reserved_stock: updatedItem.reserved_stock,
          blocked_stock: updatedItem.blocked_stock,
          expiration_date: updatedItem.expiration_date ? new Date(updatedItem.expiration_date) : null,
          lot_number: updatedItem.lot_number || '',
        });
      }
    } catch (e) {
      console.error('Error saving lot', e);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el lote' });
    } finally {
      this.isSavingLot.set(false);
    }
  }

  async deleteLot(lot: any) {
    if (lot.current_quantity > 0) {
      this.messageService.add({ severity: 'warn', summary: 'No Permitido', detail: 'No se puede eliminar un lote con stock disponible. Realice un ajuste a 0 primero.' });
      return;
    }

    const itemId = this.item()?.id;
    if (!itemId) return;

    this.confirmationService.confirm({
      message: `¿Está seguro de eliminar el registro del lote "${lot.lot_number}"?`,
      header: 'Confirmar Eliminación de Lote',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        try {
          await this.inventoryService.deleteItemLot(itemId, lot.id);
          this.messageService.add({ severity: 'success', summary: 'Eliminado', detail: 'Lote eliminado' });
          await this.loadItemLots(itemId);
        } catch (e) {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el lote' });
        }
      }
    });
  }

  getDaysUntilExpiration(dateStr: string | Date | null): number | null {
    if (!dateStr) return null;
    const exp = new Date(dateStr).getTime();
    const today = new Date().setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  // ── Premium Header Helpers ────────────────────────────────────────────────

  getItemIcon(): string {
    const rawType = this.item()?.type || this.form.get('type')?.value;
    const type = (rawType || '').toUpperCase();
    const iconMap: Record<string, string> = {
      'PRODUCT':    'pi pi-box',
      'CONSUMABLE': 'pi pi-wave-pulse',
      'REACTIVE':   'pi pi-filter',
      'STANDARD':   'pi pi-shield',
      'SPARE_PART': 'pi pi-wrench',
      'SERVICE':    'pi pi-briefcase',
      'TEST_ITEM':  'pi pi-filter'
    };
    return iconMap[type] || 'pi pi-box';
  }

  getTypeClass(): string {
    const rawType = this.item()?.type || this.form.get('type')?.value;
    const type = (rawType || '').toUpperCase();
    const classMap: Record<string, string> = {
      'PRODUCT':    'bg-blue-50 text-blue-700 border-blue-200',
      'CONSUMABLE': 'bg-purple-50 text-purple-700 border-purple-200',
      'REACTIVE':   'bg-orange-50 text-orange-700 border-orange-200',
      'STANDARD':   'bg-green-50 text-green-700 border-green-200',
      'SPARE_PART': 'bg-yellow-50 text-yellow-700 border-yellow-200',
      'SERVICE':    'bg-gray-50 text-gray-700 border-gray-200',
      'TEST_ITEM':  'bg-teal-50 text-teal-700 border-teal-200'
    };
    return classMap[type] || 'bg-gray-50 text-gray-600 border-gray-200';
  }

  getStockClass(): string {
    const stock = this.item()?.stock ?? 0;
    const min = this.item()?.min_stock ?? 0;
    const alert = this.item()?.alert_stock ?? 0;
    if (stock <= min)  return 'stat-mini-card--critical';
    if (stock <= alert) return 'stat-mini-card--warning';
    return '';
  }

  getExpirationClass(): string {
    const exp = this.item()?.expiration_date;
    if (!exp) return '';
    const days = Math.ceil((new Date(exp).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days < 0)  return 'stat-mini-card--critical';
    if (days < 30) return 'stat-mini-card--warning';
    return '';
  }

  getMainSupplier(): string {
    const suppliers = this.item()?.suppliers;
    if (suppliers && suppliers.length > 0) return suppliers[0].name;
    return 'No definido';
  }

  // ─────────────────────────────────────────────────────────────────────────

  async save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      const errorLabels: string[] = [];
      let targetTab: number | null = null;

      const fieldMeta: Record<string, { label: string; tab: number }> = {
        name: { label: 'Nombre del Artículo', tab: 0 },
        internal_code: { label: 'Código Interno', tab: 0 },
        brandId: { label: 'Marca', tab: 0 },
        formId: { label: 'Forma / Presentación', tab: 0 },
        type: { label: 'Tipo de Artículo', tab: 99 },
        stock: { label: 'Stock Actual', tab: 1 },
        min_stock: { label: 'Stock Mínimo', tab: 1 },
        alert_stock: { label: 'Punto de Pedido / Alerta', tab: 1 },
        max_stock: { label: 'Stock Máximo', tab: 1 },
        price: { label: 'Precio', tab: 2 },
        currency: { label: 'Moneda', tab: 2 },
        consumption_mode: { label: 'Modo de Consumo', tab: 0 },
        nominal_content: { label: 'Contenido Nominal', tab: 0 },
        content_unit: { label: 'Unidad de Contenido', tab: 0 },
        in_use_stability_days: { label: 'Días de Estabilidad en Uso', tab: 0 },
        container_prefix: { label: 'Prefijo de Envase', tab: 0 },
        container_numbering_mode: { label: 'Modo de Numeración de Envases', tab: 0 },
        reorder_content_threshold_percent: { label: 'Umbral de Reposición por Contenido %', tab: 0 },
        reorder_stability_percent: { label: 'Umbral de Reposición por Vigencia %', tab: 0 },
        requires_analysis: { label: 'Requiere Control de Calidad', tab: 0 },
        formulationTypeId: { label: 'Tipo de Formulación', tab: 8 },
        studyId: { label: 'Estudio GLP', tab: 8 },
        toxicology: { label: 'Clase Toxicológica', tab: 8 },
        physical_state: { label: 'Estado Físico', tab: 8 },
        received_quantity: { label: 'Cantidad Recibida', tab: 8 },
        received_unit_id: { label: 'Unidad Recibida', tab: 8 },
        service_periodicity: { label: 'Periodicidad del Servicio', tab: 0 },
        service_next_date: { label: 'Próxima Fecha de Servicio', tab: 0 }
      };

      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        if (control && control.invalid) {
          if (key === 'composition') {
            const comp = control as FormArray;
            comp.controls.forEach((ctrl, idx) => {
              if (ctrl.get('analite')?.invalid) {
                errorLabels.push(`Composición (#${idx + 1}): Principio Activo`);
              }
              if (ctrl.get('concentration')?.invalid) {
                errorLabels.push(`Composición (#${idx + 1}): Concentración`);
              }
              if (ctrl.get('unit_id')?.invalid) {
                errorLabels.push(`Composición (#${idx + 1}): Unidad`);
              }
            });
            if (targetTab === null) targetTab = 8;
          } else if (key === 'fractions') {
            const frac = control as FormArray;
            frac.controls.forEach((ctrl, idx) => {
              if (ctrl.get('name')?.invalid) {
                errorLabels.push(`Partición (#${idx + 1}): Nombre`);
              }
              if (ctrl.get('tare')?.invalid) {
                errorLabels.push(`Partición (#${idx + 1}): Peso Envase`);
              }
              if (ctrl.get('initial_mass')?.invalid) {
                errorLabels.push(`Partición (#${idx + 1}): Peso Muestra`);
              }
            });
            if (targetTab === null) targetTab = 8;
          } else {
            const meta = fieldMeta[key];
            const label = meta ? meta.label : key;
            if (!errorLabels.includes(label)) {
              errorLabels.push(label);
            }
            if (targetTab === null && meta) {
              targetTab = meta.tab;
            }
          }
        }
      });

      if (targetTab !== null) {
        this.activeTab.set(targetTab);
      }

      const detail = errorLabels.length > 0
        ? `Por favor completá los siguientes campos obligatorios o inválidos: ${errorLabels.join(', ')}.`
        : 'Completá los campos obligatorios (marcados con *) antes de guardar.';

      this.messageService.add({
        severity: 'warn',
        summary: 'Campos no válidos o incompletos',
        detail,
        life: 6000
      });
      return;
    }

    if (this.isTestItem()) {
      const comp = this.form.get('composition') as FormArray;
      if (!comp || comp.length === 0) {
        this.activeTab.set(8);
        this.messageService.add({
          severity: 'error',
          summary: 'Composición requerida',
          detail: 'Debe agregar al menos un principio activo en la composición antes de guardar el ítem de ensayo.',
          life: 4000
        });
        return;
      }

      // Check for invalid or free-text analites in composition
      const invalidAnaliteIndex = comp.controls.findIndex(ctrl => {
        const a = ctrl.get('analite')?.value;
        return !a || typeof a !== 'object' || !a.id;
      });

      if (invalidAnaliteIndex !== -1) {
        this.activeTab.set(8);
        comp.controls.forEach(ctrl => {
          ctrl.get('analite')?.markAsTouched();
          ctrl.get('analite')?.markAsDirty();
          ctrl.get('analite')?.updateValueAndValidity();
        });
        this.messageService.add({
          severity: 'error',
          summary: 'Determinación inválida',
          detail: 'No se permite texto libre en Principio Activo. Debe seleccionar una determinación válida del catálogo.',
          life: 6000
        });
        return;
      }

      const fractions = this.form.get('fractions') as FormArray;
      if (fractions && fractions.length > 0) {
        const receivedQuantity = +(this.form.get('received_quantity')?.value || 0);
        let totalFractionsMass = 0;
        fractions.getRawValue().forEach((f: any) => {
          totalFractionsMass += +(f.initial_mass || 0);
        });

        if (totalFractionsMass > receivedQuantity) {
          this.activeTab.set(8);
          this.messageService.add({
            severity: 'error',
            summary: 'Particiones inválidas',
            detail: `La suma del peso de las particiones (${totalFractionsMass}) no puede superar la Cantidad Recibida (${receivedQuantity}).`,
            life: 5000
          });
          return;
        }
      }

      if (this.item()?.id) {
        const oldComp = this.item().composition || [];
        const newComp = comp.getRawValue();

        const oldActives = oldComp.map((c: any) => c.analite?.id || c.analite_id).filter(Boolean).sort().join(',');
        const newActives = newComp.map((c: any) => c.analite?.id).filter(Boolean).sort().join(',');

        if (oldActives !== newActives) {
          this.confirmationService.confirm({
            message: 'Ha modificado la composición de los principios activos. El código interno del ítem de ensayo se regenerará y probablemente deba actualizar el código del estudio asociado. ¿Desea continuar?',
            header: 'Atención: Cambio de Composición',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sí, continuar',
            rejectLabel: 'Cancelar',
            accept: () => {
              this.form.patchValue({ internal_code: '' }); // Force backend to regenerate code
              this.executeSave();
            }
          });
          return; // Wait for user confirmation
        }
      }
    }

    this.executeSave();
  }

  private executeSave() {
    try {
      this.isSaving.set(true);
      const data = this.form.getRawValue();

      // Transform for backend
      const rawData = this.form.getRawValue();
      const payload: any = {};

      // Selectively copy fields to avoid sending unwanted ones
      const fields = [
        'name', 'commercial_name', 'internal_code', 'barcode', 'description', 'type',
        'brandId', 'formId', 'locationId', 'matrixId', 'wasteTypeId', 'wasteClassSolid', 'wasteClassLiquid', 'tags', 'price', 'currency',
        'stock', 'min_stock', 'max_stock', 'alert_stock', 'regulationIds',
        'service_periodicity', 'service_next_date', 'service_contract_start',
        'service_contract_end', 'service_scope', 'service_responsible', 'service_unit',
        'cas_number', 'lot_number', 'expiration_date', 'storage_conditions',
        'hazard_class', 'sds_url', 'std_certificate_number', 'std_valid_until',
        'std_traceability_to', 'std_uncertainty_value',
        'formulationTypeId', 'studyId', 'toxicology', 'toxicology_reference', 'physical_state', 'request_date',
        'reception_date', 'presentation', 'sponsor', 'composition', 'fractions',
        'received_quantity', 'received_unit_id', 'experimental_remaining', 'observations',
        'consumption_mode', 'nominal_content', 'content_unit', 'in_use_stability_days',
        'container_prefix', 'container_numbering_mode',
        'reorder_content_threshold_percent', 'reorder_stability_percent',
        'requires_analysis'
      ];

      fields.forEach(f => {
        if (rawData[f] !== undefined && rawData[f] !== '') {
          payload[f] = rawData[f];
        }
      });

      if (!payload.consumption_mode) {
        payload.consumption_mode = 'DISCRETE';
      }
      if (!payload.container_numbering_mode) {
        payload.container_numbering_mode = 'LOT_PREFIXED';
      }

      if (rawData.id) {
        payload.id = rawData.id;
        payload.v_ = rawData.v_;
      }

      if (rawData.supplierId) {
        payload.supplierIds = [rawData.supplierId];
      } else {
        payload.supplierIds = [];
      }

      this.onSave.emit(payload);
    } catch (error) {
      console.error("Error in component save", error);
    } finally {
      this.isSaving.set(false);
    }
  }

  toggleEdit() {
    if (!this.canManageInventory()) return;
    this.onModeChange.emit('edit');
  }

  cancel() {
    this.onCancel.emit();
  }

  back() {
    this.onBack.emit();
  }

  get canEdit() {
    return this.canManageInventory();
  }

  // Quick Create actions (can be implemented later or triggered to parent)
  addOption(type: string) {
    // For now we can emit or just log. In the future this can open a small overlay.
    console.log(`Requesting quick create for ${type}`);
  }

  async generateQRCode(item: any) {
    if (!item?.internal_code) {
      this.qrCodeImage.set(null);
      return;
    }
    try {
      const qrData = item.id ? `${window.location.origin}/inventory/items/${item.id}` : item.internal_code;
      const url = await QRCode.toDataURL(qrData, {
        margin: 1,
        width: 200,
        color: {
          dark: '#0f172a',
          light: '#ffffff'
        }
      });
      this.qrCodeImage.set(url);
    } catch (err) {
      console.error(err);
    }
  }

  printLabel() {
    const rawVal = this.form.getRawValue();
    const merged = { ...this.item(), ...rawVal };
    this.printItemData.set(merged);
    this.printPartitionData.set(null);
    setTimeout(() => {
      window.print();
    });
  }

  printPartition(index: number) {
    const fractionsArray = this.form.get('fractions') as FormArray;
    if (fractionsArray && fractionsArray.at(index)) {
      const rawVal = this.form.getRawValue();
      const merged = { ...this.item(), ...rawVal };
      this.printItemData.set(merged);
      this.printPartitionData.set(fractionsArray.at(index).getRawValue());
      setTimeout(() => {
        window.print();
      });
    }
  }

  goToLotQuality(lot: any) {
    const itemId = this.item()?.id;
    if (itemId && lot?.id) {
      this.router.navigate(['/inventory/items', itemId, 'lots', lot.id, 'quality']);
    }
  }

  // ── Fractional Containers Management Methods ──────────────────────────────

  async loadAllContainers(id?: string, passedLots?: any[]) {
    const itemId = id || this.item()?.id;
    if (!itemId) return;
    try {
      this.loadingContainers.set(true);
      const lots = passedLots || this.itemLots();
      if (!lots || lots.length === 0) {
        this.containers.set([]);
        return;
      }
      const allContainers: any[] = [];
      for (const lot of lots) {
        try {
          const lotContainers = await this.inventoryService.getItemLotContainers(itemId, lot.id);
          if (lotContainers && lotContainers.length > 0) {
            lotContainers.forEach((c: any) => {
              allContainers.push({
                ...c,
                lot_number: lot.lot_number,
                lot_internal_code: lot.internal_lot_code,
                lot_id: lot.id
              });
            });
          }
        } catch (err) {
          console.error(`Error loading containers for lot ${lot.id}`, err);
        }
      }
      this.containers.set(allContainers);
    } catch (e) {
      console.error('Error loading all containers', e);
    } finally {
      this.loadingContainers.set(false);
    }
  }

  openContainer(container: any) {
    const itemId = this.item()?.id;
    const lotId = container.lot_id;
    const containerId = container.id;
    if (!itemId || !lotId || !containerId) return;

    this.confirmationService.confirm({
      message: `¿Desea marcar el envase "${container.container_code}" como ABIERTO? Se registrará la fecha y hora de apertura e iniciará el conteo de estabilidad en uso.`,
      header: 'Confirmar Apertura de Envase',
      icon: 'pi pi-box',
      acceptLabel: 'Abrir Envase',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-primary',
      accept: async () => {
        try {
          await this.inventoryService.openContainer(itemId, lotId, containerId);
          this.messageService.add({
            severity: 'success',
            summary: 'Envase Abierto',
            detail: `El envase ${container.container_code} ahora está abierto y listo para registrar consumos.`
          });
          await this.loadItemLots(itemId);
        } catch (e: any) {
          console.error('Error opening container', e);
          this.messageService.add({ severity: 'error', summary: 'Error', detail: e?.message || 'No se pudo abrir el envase' });
        }
      }
    });
  }

  openConsumeDialog(container: any) {
    this.selectedContainer.set(container);
    this.consumeForm.reset({
      quantity: null,
      reason: '',
      sample_code: null
    });
    const maxQty = container.remaining_content != null ? container.remaining_content : container.nominal_content;
    this.consumeForm.get('quantity')?.setValidators([Validators.required, Validators.min(0.0001), Validators.max(maxQty || 999999)]);
    this.consumeForm.get('quantity')?.updateValueAndValidity();
    this.showConsumeDialog.set(true);
  }

  async confirmConsume() {
    if (this.consumeForm.invalid) {
      this.consumeForm.markAllAsTouched();
      return;
    }

    const container = this.selectedContainer();
    const itemId = this.item()?.id;
    const lotId = container?.lot_id;
    const containerId = container?.id;
    if (!itemId || !lotId || !containerId) return;

    this.isConsuming.set(true);
    try {
      const formVal = this.consumeForm.value;
      const payload = {
        quantity: Number(formVal.quantity),
        reason: formVal.reason?.trim() || 'Consumo registrado desde ficha de artículo',
        sample_code: formVal.sample_code ? Number(formVal.sample_code) : undefined
      };
      await this.inventoryService.consumeContainerPortion(itemId, lotId, containerId, payload);
      this.messageService.add({
        severity: 'success',
        summary: 'Consumo Registrado',
        detail: `Se extrajeron ${payload.quantity} ${container.content_unit || this.form.get('content_unit')?.value || ''} del envase ${container.container_code}.`
      });
      this.showConsumeDialog.set(false);
      await this.loadItemLots(itemId);
      const updatedItem = await this.inventoryService.getOne('items', itemId);
      if (updatedItem) {
        this.form.patchValue({
          stock: updatedItem.stock,
          reserved_stock: updatedItem.reserved_stock,
          blocked_stock: updatedItem.blocked_stock
        });
      }
    } catch (e: any) {
      console.error('Error consuming from container', e);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: e?.message || 'No se pudo registrar el consumo del envase' });
    } finally {
      this.isConsuming.set(false);
    }
  }

  discardContainer(container: any) {
    const itemId = this.item()?.id;
    const lotId = container.lot_id;
    const containerId = container.id;
    if (!itemId || !lotId || !containerId) return;

    this.confirmationService.confirm({
      message: `¿Está seguro de descartar el envase ${container.container_code}? El contenido remanente será dado de baja del inventario.`,
      header: 'Confirmar Descarte de Envase',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, Descartar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        try {
          await this.inventoryService.discardContainer(itemId, lotId, containerId, 'Descarte manual desde ficha de artículo');
          this.messageService.add({
            severity: 'info',
            summary: 'Envase Descartado',
            detail: `El envase ${container.container_code} fue marcado como descartado.`
          });
          await this.loadItemLots(itemId);
        } catch (err: any) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err?.message || 'No se pudo descartar el envase'
          });
        }
      }
    });
  }

  openContainerConsumptions(container: any) {
    this.selectedContainerForConsumptions.set(container);
    this.showContainerConsumptionsDialog.set(true);
  }

  goToLotQualityForContainer(container: any) {
    const itemId = this.item()?.id;
    const lotId = container?.lot_id;
    if (itemId && lotId) {
      this.router.navigate(['/inventory/items', itemId, 'lots', lotId, 'quality']);
    }
  }

  getContainerStatusLabel(status: string): string {
    switch (status) {
      case 'SEALED': return 'Sellado';
      case 'OPEN': return 'Abierto';
      case 'EXHAUSTED': return 'Agotado';
      case 'DISCARDED': return 'Descartado';
      default: return status || 'N/A';
    }
  }

  getContainerStatusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'SEALED': return 'info';
      case 'OPEN': return 'success';
      case 'EXHAUSTED': return 'secondary';
      case 'DISCARDED': return 'danger';
      default: return 'info';
    }
  }

  getContainerProgress(container: any): number {
    const nominal = container.nominal_content || container.initial_content || 1;
    const remaining = container.remaining_content != null ? container.remaining_content : nominal;
    const percent = Math.round((remaining / nominal) * 100);
    return Math.max(0, Math.min(100, percent));
  }

  onTablistWheel(event: WheelEvent) {
    if (event.deltaY !== 0) {
      const tablistEl = event.currentTarget as HTMLElement;
      const scrollContainer = (tablistEl.querySelector('.p-tablist-content') || tablistEl) as HTMLElement;
      if (scrollContainer) {
        scrollContainer.scrollLeft += event.deltaY;
        event.preventDefault();
      }
    }
  }

  isTabInvalid(tabId: number): boolean {
    if (!this.form) return false;

    if (tabId === 0) {
      const fields = ['name', 'internal_code', 'brandId', 'formId'];
      return fields.some(f => {
        const c = this.form.get(f);
        return c ? c.invalid && (c.dirty || c.touched) : false;
      });
    }

    if (tabId === 1) {
      const fields = ['stock', 'min_stock', 'alert_stock', 'max_stock', 'reorder_content_threshold_percent', 'reorder_stability_percent'];
      return fields.some(f => {
        const c = this.form.get(f);
        return c ? c.invalid && (c.dirty || c.touched) : false;
      });
    }

    if (tabId === 8) {
      const comp = this.form.get('composition') as FormArray;
      const compInvalid = comp?.controls.some(c => c.invalid && (c.dirty || c.touched)) || false;
      const frac = this.form.get('fractions') as FormArray;
      const fracInvalid = frac?.controls.some(c => c.invalid && (c.dirty || c.touched)) || false;
      return compInvalid || fracInvalid;
    }

    return false;
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
